// "What ConfirmPH verifies" — the page every trust signal should link to.
//
// Each signal states what it means AND what it does not mean, because a
// badge that only says the first half is the problem this page exists to
// fix. Nothing here is a blanket liability disclaimer and nothing is a
// guarantee.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

import { api } from "@/lib/api";
import { humanise } from "@/lib/platformLegal";
import CachePublicShell from "@/components/CachePublicShell";
import SEO from "@/components/SEO";

export default function WhatWeVerify() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/legal/what-we-verify")
      .then((r) => setData(r.data))
      .catch(() => setFailed(true));
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
         data-testid="what-we-verify">
      <CachePublicShell />
      <SEO title="What ConfirmPH verifies — ConfirmPH"
           description="What each ConfirmPH trust signal means, what it does not mean, and what
                        ConfirmPH is not." />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
        ConfirmPH · Trust
      </p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">
        What ConfirmPH verifies
      </h1>
      <p className="mt-4 text-slate-700 leading-relaxed">
        ConfirmPH shows a small number of signals about the businesses and vehicles listed on it.
        Each one is about one specific thing — business identity, business legal readiness,
        vehicle legal readiness, insurance evidence, vehicle safety, or driver and release
        readiness — and none of them is a government approval. This page says exactly what each
        one covers and, just as importantly, what it does not.
      </p>

      {!data && !failed && (
        <p className="mt-8 flex items-center gap-2 text-sm text-slate-500" role="status">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </p>
      )}
      {failed && (
        <p className="mt-8 text-sm text-slate-600" data-testid="what-we-verify-error">
          This page could not be loaded right now. Please try again shortly.
        </p>
      )}

      {data && (
        <>
          {data.evidence_states?.length > 0 && (
            <section className="mt-8" data-testid="verify-evidence-states">
              <h2 className="font-display text-xl font-semibold">How evidence is labelled</h2>
              <p className="mt-2 text-sm text-slate-600">
                ConfirmPH does not use one general &ldquo;verified&rdquo; label. Every piece of
                evidence is in one of these four states:
              </p>
              <ol className="mt-3 space-y-2">
                {data.evidence_states.map((row, i) => (
                  <li key={row.state} className="rounded-lg border border-slate-200 p-3 text-sm"
                      data-testid={`verify-evidence-state-${row.state}`}>
                    <span className="font-medium text-slate-900">{i + 1}. {row.label}</span>
                    <span className="block text-slate-700 mt-0.5">{row.meaning}</span>
                  </li>
                ))}
              </ol>
              {data.evidence_states_note && (
                <p className="mt-3 text-sm text-slate-800">{data.evidence_states_note}</p>
              )}
            </section>
          )}

          <section className="mt-8 space-y-4" data-testid="verify-signals">
            {data.signals.map((signal) => (
              <article key={signal.key} className="rounded-xl border border-slate-200 p-4"
                       data-testid={`verify-signal-${signal.key}`}>
                <h2 className="font-display text-lg font-semibold">{signal.label}</h2>
                <p className="mt-2 flex gap-2 text-sm text-slate-800">
                  <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600 mt-0.5" />
                  <span><span className="font-medium">What it means:</span> {signal.means}</span>
                </p>
                <p className="mt-2 flex gap-2 text-sm text-slate-800">
                  <XCircle className="w-4 h-4 shrink-0 text-rose-500 mt-0.5" />
                  <span>
                    <span className="font-medium">What it does not mean:</span>{" "}
                    {signal.does_not_mean}
                  </span>
                </p>
                <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
                  <div><dt className="inline font-medium">Evidence: </dt>
                    <dd className="inline">{signal.evidence}</dd></div>
                  <div><dt className="inline font-medium">Validity: </dt>
                    <dd className="inline">{signal.expires}</dd></div>
                </dl>
              </article>
            ))}
          </section>

          <section className="mt-10" data-testid="verify-not-authorities">
            <h2 className="font-display text-xl font-semibold">What ConfirmPH is not</h2>
            <ul className="mt-3 space-y-1.5 text-sm text-slate-800">
              {data.not_authorities.map((line) => (
                <li key={line} className="flex gap-2">
                  <XCircle className="w-4 h-4 shrink-0 text-slate-400 mt-0.5" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-10" data-testid="verify-merchant-states">
            <h2 className="font-display text-xl font-semibold">
              What a business&apos;s ConfirmPH state means
            </h2>
            <p className="mt-2 text-sm text-slate-600">{data.merchant_states.note}</p>
            <dl className="mt-3 space-y-2">
              {data.merchant_states.states.map((row) => (
                <div key={row.state} className="rounded-lg border border-slate-200 p-3">
                  <dt className="text-sm font-medium text-slate-900">{humanise(row.state)}</dt>
                  <dd className="text-sm text-slate-700 mt-0.5">{row.meaning}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="mt-10 space-y-3" data-testid="verify-sponsored">
            <h2 className="font-display text-xl font-semibold">Paid placement</h2>
            <p className="text-sm text-slate-800">{data.sponsored}</p>
            <h2 className="font-display text-xl font-semibold pt-2">Disagreeing with a decision</h2>
            <p className="text-sm text-slate-800">{data.appeal}</p>
          </section>
        </>
      )}

      <div className="mt-12 pt-6 border-t border-slate-200 text-sm text-slate-600 space-y-2">
        <p>
          <Link to="/report" className="underline" data-testid="verify-report-link">
            Report a problem with a listing or a vehicle
          </Link>
        </p>
        <Link to="/legal" className="underline block">All policies</Link>
        <Link to="/" className="underline block">← Back to home</Link>
      </div>
    </div>
  );
}
